// ── Subcontractor Portal Selectors ──
// Pure derivations for the Phase 1 Execution & Clarity Layer.
//
// IMPORTANT: this file is read-only on existing systems. It composes
// JobSpec, Execution, MaterialKit, and Pay Profile state — it never mutates
// them. No new persistence; no new types files for things that already have
// a home. Add selectors here when they're portal-specific.

import type { Job } from "../types";
import type { JobSpecExecution, PhaseExecution } from "../jobSpecExecution";
import type { MaterialKit } from "../materialKit";
import { isKitGateCleared } from "../materialKit";
import type { ContractorPayProfile } from "../contractorPayProfile";
import { getPayProfile } from "../contractorPayProfile";
import { resolveBoundSpec } from "../jobSpec";

export type PortalGroup = "action_required" | "scheduled" | "in_progress" | "complete";

export const PORTAL_GROUP_LABEL: Record<PortalGroup, string> = {
  action_required: "Action Required",
  scheduled: "Scheduled",
  in_progress: "In Progress",
  complete: "Complete",
};

/**
 * Filter to jobs assigned to the current contractor. Today's seed model
 * stores the assignment as a free-form string (companyName) on
 * assignedSubcontractor / assignedCrew. Match by either match key.
 */
export function filterJobsForContractor(
  jobs: Job[],
  matchKey: string,
): Job[] {
  if (!matchKey) return [];
  return jobs.filter(
    (j) => j.assignedSubcontractor === matchKey || j.assignedCrew === matchKey,
  );
}

/**
 * Group derivation rules (locked):
 *   • complete         — currentStage === "Completed" OR execution.allPhasesComplete
 *   • action_required  — kit gate cleared AND any phase is qa_failed OR
 *                        the active phase is not_started/in_progress and ready
 *                        for the installer to act (kit cleared)
 *   • in_progress      — any phase status is in_progress or submitted
 *   • scheduled        — has a future scheduledInstallDate, otherwise no
 *                        active execution
 *
 * Order matters — first-match wins.
 */
export function deriveJobGroup(args: {
  job: Job;
  execution?: JobSpecExecution;
  kit?: MaterialKit;
}): PortalGroup {
  const { job, execution, kit } = args;

  // Complete
  if (job.currentStage === "Completed") return "complete";
  if (execution?.allPhasesComplete) return "complete";

  // QA failed → always action_required
  if (execution?.phases.some((p) => p.status === "qa_failed")) {
    return "action_required";
  }

  // In progress (work submitted or actively underway)
  if (execution?.phases.some((p) => p.status === "in_progress" || p.status === "submitted")) {
    return "in_progress";
  }

  // No execution started yet — depends on whether the crew can act today.
  // If kit gate is cleared and there's a job today/past, surface as action.
  const kitCleared = isKitGateCleared(kit);
  const todayIso = new Date().toISOString().slice(0, 10);
  const isTodayOrPast = job.scheduledInstallDate && job.scheduledInstallDate <= todayIso;

  if (kitCleared && isTodayOrPast) return "action_required";
  if (job.scheduledInstallDate) return "scheduled";

  // Fallback — unscheduled assignment
  return "action_required";
}

/**
 * One-line "what to do next" hint per job. Mirrors the group rules above so
 * the card always answers the field question: "what do I do next?"
 */
export function deriveNextActionHint(args: {
  job: Job;
  execution?: JobSpecExecution;
  kit?: MaterialKit;
}): string {
  const { job, execution, kit } = args;

  if (job.currentStage === "Completed" || execution?.allPhasesComplete) {
    return "Job complete.";
  }

  const failed = execution?.phases.find((p) => p.status === "qa_failed");
  if (failed) return `QA returned ${failed.phaseName} — review correction notes.`;

  const submitted = execution?.phases.find((p) => p.status === "submitted");
  if (submitted) return `Awaiting QA on ${submitted.phaseName}.`;

  const inProgress = execution?.phases.find((p) => p.status === "in_progress");
  if (inProgress) return `Continue ${inProgress.phaseName} — finish photos & checklist.`;

  if (!isKitGateCleared(kit)) return "Material kit not cleared — do not begin install.";

  if (job.scheduledInstallDate) {
    const todayIso = new Date().toISOString().slice(0, 10);
    if (job.scheduledInstallDate <= todayIso) return "Begin demo phase on site.";
    return `Scheduled for ${job.scheduledInstallDate}.`;
  }

  return "Awaiting schedule.";
}

// ── Payout clarity ───────────────────────────────────────────────────────
//
// Source rules (locked):
//   • amount + structure: ContractorPayProfile only.
//     NEVER computeJobEconomics (margin/internal cost data).
//   • status: derived from existing fields, no new fields added.
//       - Paid       → job.payoutStatus === "Paid"
//       - Approved   → job.payoutStatus === "Approved" OR
//                      execution.allPhasesComplete (QA passed, awaiting payout)
//       - Pending QA → otherwise

export type PortalPayoutStatus = "pending_qa" | "approved" | "paid";

export const PORTAL_PAYOUT_STATUS_LABEL: Record<PortalPayoutStatus, string> = {
  pending_qa: "Pending QA",
  approved: "Approved",
  paid: "Paid",
};

export interface PortalPayoutView {
  /** Profile is the only source for amount + structure. */
  profile?: ContractorPayProfile;
  amount?: number;
  /** Display label for payment structure (full_completion → "On QA pass, full"). */
  structureLabel: string;
  status: PortalPayoutStatus;
}

export function derivePayoutView(args: {
  job: Job;
  contractorId: string;
  payProfiles: ContractorPayProfile[];
  execution?: JobSpecExecution;
}): PortalPayoutView {
  const { job, contractorId, payProfiles, execution } = args;
  const profile = getPayProfile(payProfiles, contractorId);

  const payoutStatus = job.financials?.payoutStatus;
  const status: PortalPayoutStatus =
    payoutStatus === "Paid"
      ? "paid"
      : payoutStatus === "Approved" || execution?.allPhasesComplete
      ? "approved"
      : "pending_qa";

  const structureLabel =
    profile?.paymentStructure === "full_completion"
      ? "Full payout on final QA pass"
      : "—";

  return {
    profile,
    amount: profile?.totalPayoutAmount,
    structureLabel,
    status,
  };
}

// ── Active phase view ─────────────────────────────────────────────────────

/** Returns the first non-passed phase (the one the installer should be on). */
export function getActivePhaseExec(execution?: JobSpecExecution): PhaseExecution | undefined {
  if (!execution) return undefined;
  return execution.phases.find((p) => p.status !== "qa_passed");
}

/**
 * QA feedback rule (CRITICAL):
 *   • Show qaFailReason ONLY when the current phase status is "qa_failed".
 *   • Once the phase is qa_passed (or even back to in_progress/submitted),
 *     qaFailReason is a tombstone for outcome derivation — never an active
 *     failure to surface to the installer.
 */
export function getActiveQaFailReason(phase?: PhaseExecution): string | undefined {
  if (!phase) return undefined;
  if (phase.status !== "qa_failed") return undefined;
  return phase.qaFailReason?.trim() || undefined;
}

/** Light helper for portal display of bound spec (snapshot-aware). */
export function getPortalSpec(job: Job) {
  return resolveBoundSpec(job.boundJobSpec);
}
